import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';
import './Auth.css';

const LoginPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresRoleSelection, setRequiresRoleSelection] = useState(false);
  const [googleCredential, setGoogleCredential] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Simple browser fingerprint (canvas + audio + fonts)
  const generateBrowserFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Browser Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Browser Fingerprint', 4, 17);
    const canvasHash = canvas.toDataURL().slice(-50);

    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const platform = navigator.platform;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;

    const combined = `${canvasHash}${userAgent}${language}${platform}${screenResolution}`;

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return hash.toString(36);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const fingerprint = generateBrowserFingerprint();

      const result = await loginWithGoogle(
        credentialResponse.credential,
        selectedRole,
        fingerprint
      );

      if (result.success) {
        if (result.isNewUser) {
          console.log('Welcome! Created new account with 7-day trial');
        }
        navigate('/home');
      } else if (result.requiresRole) {
        // New user - needs to select role
        setGoogleCredential(credentialResponse.credential);
        setRequiresRoleSelection(true);
        setLoading(false);
      } else {
        setLoading(false);
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setLoading(false);
      setError('An unexpected error occurred. Please try again.');
      console.error('Google login error:', err);
    }
  };

  const handleRoleSelection = async (role) => {
    setSelectedRole(role);
    setError('');
    setLoading(true);

    try {
      const fingerprint = generateBrowserFingerprint();

      const result = await loginWithGoogle(
        googleCredential,
        role,
        fingerprint
      );

      if (result.success) {
        console.log(`Welcome! Created new ${role} account with 7-day trial`);
        navigate('/home');
      } else {
        setLoading(false);
        setError(result.error || 'Failed to create account. Please try again.');
      }
    } catch (err) {
      setLoading(false);
      setError('An unexpected error occurred. Please try again.');
      console.error('Role selection error:', err);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In failed. Please try again.');
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="app-icon">🌍</div>
          <h1>Teacha</h1>
          <p className="subtitle">Language Tutor App</p>
        </div>

        {!requiresRoleSelection ? (
          <>
            <div className="welcome-card">
              <h2>Sign up / Sign in</h2>
              <p>Use your Google account to get started</p>
              <p style={{ fontSize: '14px', marginTop: '12px', color: 'var(--primary-400)' }}>
                ✨ Start your <strong>7-day free trial</strong> today!
              </p>
            </div>

            {error && (
              <div className="error-card">
                {error}
              </div>
            )}

            <div className="auth-form">
              <GoogleSignInButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                disabled={loading}
                text={loading ? 'Signing in...' : 'Continue with Google'}
                theme="filled_black"
              />
            </div>

            <div className="terms-text">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </div>
          </>
        ) : (
          <>
            <div className="welcome-card">
              <h2>Choose Your Role</h2>
              <p>Are you joining as a student or teacher?</p>
            </div>

            {error && (
              <div className="error-card">
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => handleRoleSelection('student')}
                disabled={loading}
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--primary-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading && selectedRole === 'student' ? 'Creating account...' : '🎓 I\'m a Student'}
              </button>

              <button
                onClick={() => handleRoleSelection('teacher')}
                disabled={loading}
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--primary-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading && selectedRole === 'teacher' ? 'Creating account...' : '👨‍🏫 I\'m a Teacher'}
              </button>
            </div>

            <div style={{ marginTop: '16px', fontSize: '14px', color: 'var(--neutral-400)', textAlign: 'center' }}>
              <p>Both roles get a <strong>7-day free trial</strong></p>
              <p style={{ marginTop: '8px' }}>Students: Practice languages with tutors</p>
              <p>Teachers: Help students learn and earn money</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
